import React from 'react';
import moment from 'moment';
import InstitutionBadge from '../InstitutionBadge.jsx';
import AccountIcon from './AccountIcon.jsx';
import { accountIdentity } from '../../../utilities/accountIdentity.js';
import { moneyFormatFromCents } from '../../../utilities/moneyFormat';

/**
 * One account, drawn with what tells it apart from the others: its institution's badge, its icon in
 * its colour, the name the budget gave it, and beneath that the institution, the last digits, the
 * type and the latest balance with its date. Several accounts here share a name outright -- two
 * "RothIra", a handful of Citi cards -- and the name alone cannot say which is which.
 *
 * An account with no digits that shares its name with another carries a `hint` instead (twinHints()),
 * drawn where the digits would be.
 *
 * `compact` is a single line (name and digits), for a select's chosen value. Otherwise two lines, for
 * menu options and lists.
 *
 * Takes the account in any shape accountIdentity() reads, or an already worked out `identity`.
 * `institution` is the group the account sits in, when the caller holds it.
 */
export default function AccountLabel({ account = null, identity = null, institution = null, compact = false, className = '' }) {
	const shown = identity || accountIdentity(account, institution);
	const badgeSize = compact ? 18 : 24;

	return (
		<div className={`account_label ${compact ? 'compact' : 'full'} ${shown.closed ? 'closed' : ''} ${className}`.trim()}>
			<InstitutionBadge
				institution={shown.badge.institution}
				name={shown.badge.name}
				color={shown.badge.color}
				size={badgeSize}
			/>
			<div className='account_label_text'>
				<div className='account_label_primary'>
					<AccountIcon icon={shown.icon} color={shown.color} size={compact ? 14 : 16} />
					<span className='account_label_name'>{shown.name}</span>
					{compact && !!shown.mask && <span className='account_label_mask'>{shown.mask}</span>}
					{compact && !shown.mask && !!shown.hint && <span className='account_label_hint'>{shown.hint}</span>}
					{shown.closed && <span className='account_label_closed'>Closed</span>}
				</div>
				{!compact &&
				<div className='account_label_secondary'>
					{!!shown.originalName && <span className='account_label_original' title='What the bank calls it'>{shown.originalName}</span>}
					{!!shown.institutionName && <span className='account_label_institution'>{shown.institutionName}</span>}
					{!!shown.mask && <span className='account_label_mask'>{shown.mask}</span>}
					{!shown.mask && !!shown.hint && <span className='account_label_hint'>{shown.hint}</span>}
					{!!shown.typeLabel && <span className='account_label_type'>{shown.typeLabel}</span>}
					{shown.balance !== null
						? <span className='account_label_balance'>
							{moneyFormatFromCents(shown.balance)}
							{!!shown.balanceDate && <> as of {formatDay(shown.balanceDate)}</>}
						</span>
						: !!shown.lastActivity && <span className='account_label_activity'>last activity {formatDay(shown.lastActivity)}</span>
					}
				</div>
				}
			</div>
		</div>
	);
}

function formatDay(day) {
	return moment(day, 'YYYY-MM-DD').format('MMM D, YYYY');
}
